import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Loader2, CreditCard, Calendar, AlertTriangle, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import SeatManagementCard from './SeatManagementCard';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Plan display data — must match backend billing.py
const PLAN_NAMES = {
  start: { label: 'Start', desc: 'Grundpaketet för entreprenörer' },
  pro: { label: 'Pro', desc: 'Mest populär — allt du behöver' },
  business: { label: 'Business', desc: 'Full kontroll & support' },
};

const SubscriptionTab = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/subscription/status`, { withCredentials: true });
      setState(res.data);
    } catch (err) {
      toast.error('Kunde inte hämta prenumerationsstatus');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API_URL}/subscription/cancel`, {}, { withCredentials: true });
      if (res.data.is_in_trial) {
        toast.success('Prenumerationen är uppsagd — du loggas ut.');
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      } else {
        toast.success('Uppsägning registrerad — åtkomst t.o.m. periodens slut.');
        setShowCancelModal(false);
        fetchStatus();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte säga upp prenumerationen');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await axios.post(`${API_URL}/subscription/reactivate`, {}, { withCredentials: true });
      toast.success('Uppsägning återtagen.');
      fetchStatus();
    } catch (err) {
      toast.error('Kunde inte återta uppsägningen');
    } finally {
      setReactivating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-[#E8613A] animate-spin" />
      </div>
    );
  }

  if (!state || state.status === 'none') {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center max-w-lg" data-testid="no-subscription">
        <CreditCard size={32} className="text-muted-foreground/40 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Ingen prenumeration kopplad till detta konto.</p>
      </div>
    );
  }

  const [planTier, contractMonths] = (state.plan || '').split('_');
  const planInfo = PLAN_NAMES[planTier] || { label: planTier, desc: '' };
  const trialEnd = state.trial_end ? new Date(state.trial_end) : null;
  const periodEnd = state.current_period_end ? new Date(state.current_period_end) : null;

  const statusStyles = {
    trial: { label: 'Provperiod', bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-900' },
    trialing: { label: 'Provperiod', bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-900' },
    active: { label: 'Aktiv', bg: 'bg-green-50 dark:bg-green-950/30', text: 'text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-900' },
    paused: { label: 'Pausad', bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-900' },
    cancelled: { label: 'Uppsagd', bg: 'bg-zinc-100 dark:bg-zinc-900', text: 'text-zinc-600 dark:text-zinc-400', border: 'border-zinc-200 dark:border-zinc-800' },
    pending: { label: 'Väntar', bg: 'bg-zinc-100 dark:bg-zinc-900', text: 'text-zinc-600 dark:text-zinc-400', border: 'border-zinc-200 dark:border-zinc-800' },
  };
  const st = statusStyles[state.status] || statusStyles.pending;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="subscription-tab">
      {/* Current plan card */}
      <div className={`bg-card border-2 ${st.border} rounded-2xl overflow-hidden`}>
        <div className={`${st.bg} px-6 py-4 flex items-center justify-between border-b ${st.border}`}>
          <div className="flex items-center gap-3">
            <CreditCard size={18} className={st.text} />
            <div>
              <p className="text-base font-bold text-foreground">Operio {planInfo.label}</p>
              <p className="text-xs text-muted-foreground">{contractMonths ? `${contractMonths} månaders bindningstid` : ''}</p>
            </div>
          </div>
          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${st.text} bg-card border ${st.border}`} data-testid="sub-status-badge">
            {st.label}
          </span>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoRow label="Anställda" value={`${state.employee_count} st`} />
            {state.status === 'trial' && trialEnd && (
              <InfoRow
                label="Provperiod slutar"
                value={trialEnd.toLocaleDateString('sv-SE')}
                subtitle={state.days_remaining !== null ? `${state.days_remaining} dag${state.days_remaining === 1 ? '' : 'ar'} kvar` : null}
                icon={<Calendar size={14} />}
              />
            )}
            {state.status === 'active' && periodEnd && (
              <InfoRow
                label="Nästa debitering"
                value={periodEnd.toLocaleDateString('sv-SE')}
                icon={<Calendar size={14} />}
              />
            )}
            {state.agreement_accepted && (
              <InfoRow label="Avtal godkänt" value="Ja" icon={<Check size={14} />} />
            )}
          </div>

          {/* Status-specific call-outs */}
          {state.status === 'paused' && (
            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle size={16} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-900 dark:text-red-200">Betalningen misslyckades</p>
                <p className="text-xs text-red-800 dark:text-red-300 mt-1">Uppdatera ditt kort hos Stripe för att låsa upp Operio.</p>
              </div>
            </div>
          )}

          {state.cancel_at_period_end && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-2">
                <AlertTriangle size={14} /> Uppsägning planerad
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                Prenumerationen avslutas {periodEnd ? periodEnd.toLocaleDateString('sv-SE') : '—'}. Ångra om du ändrat dig.
              </p>
              <Button
                onClick={handleReactivate}
                disabled={reactivating}
                size="sm"
                variant="outline"
                className="mt-3 h-8 text-xs border-amber-300"
                data-testid="sub-reactivate-btn"
              >
                {reactivating ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : null}
                Ångra uppsägning
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Seat management for extra users */}
      <SeatManagementCard />

      {/* Cancel action */}
      {(state.status === 'trial' || state.status === 'trialing' || state.status === 'active') && !state.cancel_at_period_end && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h4 className="text-sm font-bold text-foreground">Säg upp prenumerationen</h4>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
            {(state.status === 'trial' || state.status === 'trialing')
              ? 'Under provperioden kan du säga upp helt gratis. Tjänsten avslutas direkt och inget kort debiteras.'
              : 'Du fortsätter ha åtkomst till slutet av nuvarande period. Bindningstid honoreras av Stripe.'}
          </p>
          <Button
            onClick={() => setShowCancelModal(true)}
            variant="outline"
            className="mt-4 h-9 text-xs border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
            data-testid="sub-cancel-btn"
          >
            Säg upp prenumeration
          </Button>
        </div>
      )}

      {state.status === 'cancelled' && (
        <div className="bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-xl p-5 text-center">
          <p className="text-sm text-muted-foreground">Prenumerationen är uppsagd.</p>
        </div>
      )}

      {/* Cancel confirmation modal */}
      {showCancelModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4"
          onClick={() => setShowCancelModal(false)}
          data-testid="sub-cancel-modal"
        >
          <div className="max-w-md w-full bg-card rounded-2xl border border-border shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#1A1A1A] text-white px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle size={18} className="text-amber-400" />
                <h2 className="text-base font-semibold">Säg upp prenumerationen?</h2>
              </div>
              <button onClick={() => setShowCancelModal(false)} className="hover:bg-white/10 rounded p-1">
                <X size={16} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm text-foreground">
              {(state.status === 'trial' || state.status === 'trialing') ? (
                <>
                  <p className="leading-relaxed">
                    Du är i provperioden (slutar <strong>{trialEnd?.toLocaleDateString('sv-SE')}</strong>).
                    Tjänsten avslutas <strong>direkt</strong> och inget kort debiteras.
                  </p>
                  <p className="text-xs text-muted-foreground">Du loggas ut efter uppsägning.</p>
                </>
              ) : (
                <p className="leading-relaxed">
                  Du behåller åtkomst t.o.m. <strong>{periodEnd?.toLocaleDateString('sv-SE')}</strong>.
                  Därefter avslutas tjänsten och inga fler debiteringar görs.
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between">
              <button onClick={() => setShowCancelModal(false)} className="text-sm text-muted-foreground hover:text-foreground" data-testid="sub-cancel-modal-back">
                Avbryt
              </button>
              <Button
                onClick={handleCancel}
                disabled={cancelling}
                variant="destructive"
                className="h-10 px-5 rounded-full"
                data-testid="sub-cancel-modal-confirm"
              >
                {cancelling ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" /> Säger upp...</>
                ) : (
                  (state.status === 'trial' || state.status === 'trialing') ? 'Ja, säg upp direkt' : 'Ja, säg upp'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoRow = ({ label, value, subtitle, icon }) => (
  <div>
    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium flex items-center gap-1.5">
      {icon}{label}
    </p>
    <p className="text-base font-semibold text-foreground mt-0.5">{value}</p>
    {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
  </div>
);

export default SubscriptionTab;
